// @flow
import 'svgxuse';

import React from 'react';
import {
  Route,
  Switch,
  Redirect,
  BrowserRouter,
  useHistory
} from 'react-router-dom';

import { ComponentThatThrows } from 'shared/components/ComponentThatThrows';
import ContainerWithAside from 'shared/components/ContainerWithAside';
import { ErrorBoundary } from 'shared/components/ErrorBoundary';
import { ErrorPage } from 'shared/components/ErrorPage';
import { SageAuthorizationConfirmation } from 'shared/components/SageAuthorizationConfirmation';
import ScrollTopOnRouteChange from 'shared/components/ScrollTopOnRouteChange';
import { FormFieldsProvider } from 'shared/formContext/FormFieldsProvider';
import { OptimizelyProvider } from 'shared/optimizely/OptimizelyProvider';
import ApplicationSidebar from 'shared/partials/ApplicationSidebar';
import {
  LoanApplication,
  submitApplication
} from 'shared/requests/application';
import { TrackingProvider } from 'shared/tracking/TrackingContext';
import { setApplicationId } from 'shared/utils/applicationId';
import { handleAuthFailure } from 'shared/utils/handleAuthFailure';
import { logger } from 'shared/utils/logger';
import BusinessDetails from './steps/BusinessDetails';
import ContactDetails from './steps/ContactDetails';
import LoanAmountAndTerm from './steps/LoanAmountAndTerm';
import { sendToLogin } from 'shared/utils/helpers';
import Layout from 'shared/components/Layout';

export const makeSubmit =
  (push: (string, { message: ?string }) => void) =>
  async (state: LoanApplication) => {
    try {
      const { application_id } = await submitApplication(state);
      setApplicationId(application_id);

      window.location.assign('/uk/borrower/loan-application');
    } catch (error) {
      handleAuthFailure(error).or(async () => {
        if (error.status === 422) {
          try {
            const contentType = error.headers.get('Content-Type');
            if (!contentType.includes('application/json')) throw new Error();
            // status 422 should always have the `errors` attribute in the JSON
            const { errors } = await error.json();

            if (errors && errors.user_has_in_flight_loan_application) {
              sendToLogin();
            } else {
              push('/error', {});
            }
          } catch (e) {
            logger.error('Error reading eligibility check error body', e);
            push('/error', {});
          }
        } else {
          logger.error('Error submitting eligibility check', error);
          // TODO We will use a custom error message at some point in the future
          const message = error.name === 'AbortError' ? null : null;
          push('/error', { message });
        }
      });
    }
  };

export const LOCAL_STORAGE_KEY = 'loan-application-eligibility';

export const Eligibility = () => {
  const { push } = useHistory();
  const noSidebarContentPaths = ['/rejection'];

  return (
    <Layout>
      <ErrorBoundary>
        <TrackingProvider>
          {/* // $FlowFixMe */}
          <FormFieldsProvider
            localStorageKey={LOCAL_STORAGE_KEY}
            submit={makeSubmit(push)}>
            <>
              <ContainerWithAside
                content={
                  <OptimizelyProvider>
                    <Switch>
                      <Route
                        exact
                        path='/sage-authorization-confirmation'
                        component={SageAuthorizationConfirmation}
                      />
                      <Route
                        exact
                        path='/loan-amount-and-term'
                        component={LoanAmountAndTerm}
                      />
                      <Route
                        exact
                        path='/business-details'
                        component={BusinessDetails}
                      />
                      <Route
                        exact
                        path='/contact-details'
                        component={ContactDetails}
                      />
                      <Route exact path='/error' component={ErrorPage} />
                      <Route
                        exact
                        path='/_private/force-error'
                        component={ComponentThatThrows}
                      />
                      <Redirect to='/loan-amount-and-term' />
                    </Switch>
                  </OptimizelyProvider>
                }
                asideContent={
                  <Route
                    render={({ location }) =>
                      !noSidebarContentPaths.includes(location.pathname) && (
                        <ApplicationSidebar />
                      )
                    }
                  />
                }
                themeName='contrast'
              />
            </>
          </FormFieldsProvider>
        </TrackingProvider>
      </ErrorBoundary>
    </Layout>
  );
};

export default function EligibilityWithRouter() {
  return (
    <BrowserRouter basename='/uk/borrower/eligibility'>
      <ScrollTopOnRouteChange />
      <Route component={Eligibility} />
    </BrowserRouter>
  );
}
